import { useState, useEffect } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import { intervalToDuration } from 'date-fns'
import IconArrowDown from '@/components/icons/IconArrowDown'
import {
  marketInfoValue,
  marketTickerValue,
  indexPriceMapState,
  markPriceMapState,
  futuresMarketOpenInterestMapState,
  futuresMarketInfoMapState
} from '@/recoil/Market'
import numberFormat from '@/utils/numberFormat'

function formatTwoDigits(s) {
  return String(s).padStart(2, '0')
}

function Timer({ endTimestamp }) {
  const [countdown, setCountdown] = useState(
    intervalToDuration({
      start: new Date(),
      end: new Date(endTimestamp)
    })
  )
  const [refetchTrigger, setRefetchTrigger] = useState(false)

  useEffect(() => {
    let timer = setTimeout(() => setRefetchTrigger(v => !v), 1000)
    setCountdown(
      intervalToDuration({
        start: new Date(),
        end: new Date(endTimestamp)
      })
    )
    return () => clearTimeout(timer)
  }, [endTimestamp, refetchTrigger])

  const { hours, minutes, seconds } = countdown
  return (
    <>{`${formatTwoDigits(hours)}:${formatTwoDigits(minutes)}:${formatTwoDigits(
      seconds
    )}`}</>
  )
}

export default function MarketTicker() {
  const [marketOpenInterestMap] = useRecoilState(
    futuresMarketOpenInterestMapState
  )
  const [futuresMarketInfoMap] = useRecoilState(futuresMarketInfoMapState)
  const [markPriceMap] = useRecoilState(markPriceMapState)
  const [indexPriceMap] = useRecoilState(indexPriceMapState)
  const marketInfo = useRecoilValue(marketInfoValue)
  const marketTicker = useRecoilValue(marketTickerValue)
  const priceFormat = marketInfo.priceDecimal || 0
  const sizeFormat = marketInfo.sizeDecimal || 0
  const change = BigNumber(marketTicker.open).minus(marketTicker.close)
  const changePercentage = BigNumber(change)
    .abs()
    .div(marketTicker.open)
    .times(100)
    .toNumber()

  const spotMarketSymbol = `SPOT_${marketInfo.baseCurrency}_${marketInfo.quoteCurrency}`
  const markPriceReady = markPriceMap[marketInfo.symbol] !== undefined
  const indexPriceReady = indexPriceMap[spotMarketSymbol] !== undefined
  const tickerReady = Object.keys(marketTicker).length > 0
  const futuresMarketInfo = futuresMarketInfoMap[marketInfo.symbol]

  return (
    <div
      className={clsx('market-ticker', {
        'is-futures': marketInfo.isFutures
      })}
    >
      <div className="market-ticker-cell">
        <div className="market-ticker-label">Change</div>
        <div
          className={clsx('market-ticker-value', {
            'color-positive': change.lt(0),
            'color-negative': change.gt(0)
          })}
        >
          {tickerReady ? (
            <>
              {!change.eq(0) && (
                <IconArrowDown
                  className={clsx('price-change-icon', {
                    'is-up': change.lt(0)
                  })}
                />
              )}
              {numberFormat(changePercentage, 2)}%
            </>
          ) : (
            '-'
          )}
        </div>
      </div>
      {marketInfo.isFutures ? (
        <>
          <div className="market-ticker-cell">
            <div className="market-ticker-label">Mark</div>
            <div className="market-ticker-value">
              {markPriceReady
                ? numberFormat(markPriceMap[marketInfo.symbol], priceFormat)
                : '-'}
            </div>
          </div>
          <div className="market-ticker-cell">
            <div className="market-ticker-label">Index</div>
            <div className="market-ticker-value">
              {indexPriceReady
                ? numberFormat(indexPriceMap[spotMarketSymbol], priceFormat)
                : '-'}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="market-ticker-cell">
            <div className="market-ticker-label">High</div>
            <div className="market-ticker-value">
              {tickerReady ? numberFormat(marketTicker.high, priceFormat) : '-'}
            </div>
          </div>
          <div className="market-ticker-cell">
            <div className="market-ticker-label">Low</div>
            <div className="market-ticker-value">
              {tickerReady ? numberFormat(marketTicker.low, priceFormat) : '-'}
            </div>
          </div>
        </>
      )}
      <div className="market-ticker-cell">
        <div className="market-ticker-label">Vol</div>
        <div className="market-ticker-value">
          {tickerReady ? (
            <>
              $
              {numberFormat(marketTicker.amount, {
                formatKey: 'max2',
                compact: true
              })}
            </>
          ) : (
            '-'
          )}
        </div>
      </div>
      {marketInfo.isFutures && (
        <>
          <div className="market-ticker-cell">
            <div className="market-ticker-label">Funding Rate</div>
            <div className="market-ticker-value">
              <span className="funding-rate-highlight">
                {futuresMarketInfo
                  ? `${BigNumber(futuresMarketInfo.est_funding_rate).times(
                      100
                    )}%`
                  : '-'}
              </span>
              /{' '}
              {futuresMarketInfo ? (
                <Timer endTimestamp={futuresMarketInfo.next_funding_time} />
              ) : (
                '-'
              )}
            </div>
          </div>
          <div className="market-ticker-cell">
            <div className="market-ticker-label">Open Interest</div>
            <div className="market-ticker-value">
              {marketOpenInterestMap[marketInfo.symbol]
                ? `${numberFormat(
                    marketOpenInterestMap[marketInfo.symbol],
                    sizeFormat
                  )} ${marketInfo.baseCurrency}`
                : '-'}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
